import React, { useState, useEffect } from 'react';
import { 
  Tab, Nav, Row, Col, Spinner, 
  Table, Button, Badge, Form, 
  Alert, InputGroup, Card, Modal, FormControl
} from 'react-bootstrap';
import { 
  FaSearch, FaFilter, FaMoneyBillWave, FaCalendarAlt,
  FaCheck, FaUserTie, FaExchangeAlt, FaEye,
  FaBan, FaClock, FaFileDownload, FaUser, FaEdit
} from 'react-icons/fa';
import axios from 'axios';
import { Link } from 'react-router-dom';
import BookingDetailsModal from './BookingDetailsModal';
import AssignDriverModal from './AssignDriverModal';
import RefundModal from './RefundModal';

const AllBookings = ({ defaultTab = 'all' }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editModalShow, setEditModalShow] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editBookingId, setEditBookingId] = useState(null);

  const fetchBookings = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const params = {
        status: activeTab === 'all' ? undefined : activeTab,
        search: searchTerm,
        page,
        ...filters
      };
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/bookings`, {
        headers: { 'x-auth-token': token },
        params
      });
      
      setBookings(response.data.bookings);
      setTotalPages(response.data.totalPages);
      setTotalBookings(response.data.totalBookings);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.response?.data?.error || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBookings();
    }, 500);
    return () => clearTimeout(timer);
  }, [activeTab, searchTerm, filters]);

  const handleStatusChange = async (bookingId, status) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.put(`${process.env.REACT_APP_API_URL}/admin/bookings/${bookingId}/status`,
        { status },
        { headers: { 'x-auth-token': token } }
      );
      
      fetchBookings(currentPage);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleAssignDriver = (booking) => {
    setSelectedBooking(booking);
    setShowAssignModal(true);
  };

  const handleRefund = (booking) => {
    setSelectedBooking(booking);
    setShowRefundModal(true);
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleDownloadInvoice = (invoiceUrl) => {
    if (!invoiceUrl) return;
    window.open(invoiceUrl, '_blank');
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchBookings(1);
  };

  const handleFilterReset = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
    fetchBookings(1);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge bg="warning">Pending</Badge>;
      case 'confirmed': return <Badge bg="primary">Confirmed</Badge>;
      case 'assigned': return <Badge bg="info">Assigned</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default: return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    try {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timeString;
    }
  };

  const renderCustomerInfo = (booking) => {
    return (
      <div className="d-flex align-items-center">
        <FaUser className="me-2" />
        <div>
          <div>{booking.customerName || 'Customer'}</div>
          {booking.customerPhone && <small className="text-muted d-block">{booking.customerPhone}</small>}
          {booking.customerEmail && <small className="text-muted d-block">{booking.customerEmail}</small>}
        </div>
      </div>
    );
  };

  const renderDriverInfo = (driver) => {
    if (!driver) return <Badge bg="secondary">Not Assigned</Badge>;
    
    return (
      <div className="d-flex align-items-center">
        <FaUserTie className="me-2" />
        <div>
          <div>{driver.name}</div>
          {driver.phone && <small className="text-muted">{driver.phone}</small>}
        </div>
      </div>
    );
  };

  const handleEditClick = (bookingId, field, currentValue) => {
    setEditBookingId(bookingId);
    setEditingField(field);
    setEditValue(currentValue);
    setEditModalShow(true);
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const updateData = {};
      updateData[editingField] = editValue;

      await axios.put(`${process.env.REACT_APP_API_URL}/admin/bookings/${editBookingId}`, 
        updateData,
        { headers: { 'x-auth-token': token } }
      );

      setEditModalShow(false);
      fetchBookings(currentPage);
    } catch (err) {
      console.error('Error updating booking:', err);
      setError(err.response?.data?.error || 'Failed to update booking');
    } finally {
      setLoading(false);
    }
  };

  const renderEditableField = (bookingId, field, value, isDate = false) => {
    return (
      <div 
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer' }}
        onClick={() => handleEditClick(bookingId, field, value)}
      >
        <span>{isDate ? formatDate(value) : formatTime(value)}</span>
        <FaEdit className="ms-2" size={12} />
      </div>
    );
  };

  return (
    <div className="bookings-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Booking Management</h2>
        <div>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowFilters(!showFilters)}
            className="me-2"
          >
            <FaFilter className="me-1" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Link to="/admin/bookings/new" className="btn btn-primary">
            <FaCalendarAlt className="me-1" />
            New Booking
          </Link>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {showFilters && (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleFilterSubmit}>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date From</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date To</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={filters.dateTo}
                      onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Min Amount</Form.Label>
                    <Form.Control 
                      type="number" 
                      placeholder="Min" 
                      value={filters.minAmount}
                      onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Max Amount</Form.Label>
                    <Form.Control 
                      type="number" 
                      placeholder="Max" 
                      value={filters.maxAmount}
                      onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-3">
                <Button variant="primary" type="submit" className="me-2">
                  Apply Filters
                </Button>
                <Button variant="outline-secondary" onClick={handleFilterReset}>
                  Reset Filters
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search bookings by ID, name, email or phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="all">All Bookings ({totalBookings})</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pending">Pending ({bookings.filter(b => b.bookingStatus === 'pending').length})</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="confirmed">Confirmed ({bookings.filter(b => b.bookingStatus === 'confirmed').length})</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="assigned">Assigned ({bookings.filter(b => b.bookingStatus === 'assigned').length})</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="completed">Completed ({bookings.filter(b => b.bookingStatus === 'completed').length})</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="cancelled">Cancelled ({bookings.filter(b => b.bookingStatus === 'cancelled').length})</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey={activeTab}>
            {loading ? (
              <div className="text-center my-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Customer</th>
                        <th>Pickup Date</th>
                        <th>Pickup Time</th>
                        <th>Vehicle</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Driver</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length > 0 ? (
                        bookings.map((booking) => (
                          <tr key={booking._id}>
                            <td>
                              <Button 
                                variant="link" 
                                onClick={() => handleViewDetails(booking)}
                                className="p-0"
                              >
                                {booking._id}
                              </Button>
                            </td>
                            <td>{renderCustomerInfo(booking)}</td>
                            <td>
                              {renderEditableField(booking._id, 'pickupDate', booking.pickupDate, true)}
                            </td>
                            <td>
                              {renderEditableField(booking._id, 'pickupTime', booking.pickupTime)}
                            </td>
                            <td>{booking.vehicleName}</td>
                            <td>${booking.totalAmount}</td>
                            <td>
                              {getStatusBadge(booking.bookingStatus)}
                              {booking.invoice && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleDownloadInvoice(booking.invoice)}
                                  title="Download Invoice"
                                  className="ms-2 p-0"
                                >
                                  <FaFileDownload className="text-primary" />
                                </Button>
                              )}
                            </td>
                            <td>
                              {renderDriverInfo(booking.driver)}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <Button
                                  variant="info"
                                  size="sm"
                                  onClick={() => handleViewDetails(booking)}
                                  title="View Details"
                                >
                                  <FaEye />
                                </Button>
                                
                                {booking.bookingStatus === 'pending' && (
                                  <>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => handleStatusChange(booking._id, 'confirmed')}
                                      title="Confirm Booking"
                                    >
                                      <FaCheck />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleRefund(booking)}
                                      title="Cancel Booking"
                                    >
                                      <FaBan />
                                    </Button>
                                  </>
                                )}
                                
                                {booking.bookingStatus === 'confirmed' && (
                                  <>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => handleAssignDriver(booking)}
                                      title="Assign Driver"
                                    >
                                      <FaUserTie />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleRefund(booking)}
                                      title="Cancel Booking"
                                    >
                                      <FaBan />
                                    </Button>
                                  </>
                                )}
                                
                                {booking.bookingStatus === 'assigned' && (
                                  <>
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      onClick={() => handleAssignDriver(booking)}
                                      title="Reassign Driver"
                                    >
                                      <FaExchangeAlt />
                                    </Button>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => handleStatusChange(booking._id, 'completed')}
                                      title="Mark as Completed"
                                    >
                                      <FaCheck />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleRefund(booking)}
                                      title="Cancel Booking"
                                    >
                                      <FaBan />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No bookings found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => fetchBookings(currentPage - 1)}
                          >
                            Previous
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <li 
                            key={page} 
                            className={`page-item ${currentPage === page ? 'active' : ''}`}
                          >
                            <button 
                              className="page-link" 
                              onClick={() => fetchBookings(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => fetchBookings(currentPage + 1)}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Modal show={editModalShow} onHide={() => setEditModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit {editingField === 'pickupDate' ? 'Pickup Date' : 'Pickup Time'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingField === 'pickupDate' ? (
            <FormControl
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          ) : (
            <FormControl
              type="time"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModalShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <BookingDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        booking={selectedBooking}
        onUpdate={(updatedBooking) => {
          setSelectedBooking(updatedBooking);
          fetchBookings(currentPage);
        }}
      />
      
      <AssignDriverModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        booking={selectedBooking}
        refreshBookings={() => fetchBookings(currentPage)}
      />
      
      <RefundModal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        booking={selectedBooking}
        refreshBookings={() => fetchBookings(currentPage)}
      />
    </div>
  );
};

export default AllBookings;